import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { I18nString } from '../../../models/permit-category.model';
import { PermitStatusType } from '../../../constants/permit.constants';

/**
 * The status being moved to. Shaped after `AllowedStatusTransition`, which is what the picker now
 * hands over — hence `terminal` being optional: the permit-details response does not carry it per
 * transition, so the "final status" warning only renders when a caller happens to know the flag.
 */
export interface PermitStatusChangeDialogTarget {
    _id: string;
    type?: string;
    name: I18nString | string;
    color: string;
    terminal?: boolean;
}

export interface PermitStatusChangeDialogData {
    targetStatus: PermitStatusChangeDialogTarget;
}

@Component({
    selector: 'app-permit-status-change-dialog',
    templateUrl: './permit-status-change-dialog.component.html',
    styleUrls: ['./permit-status-change-dialog.component.scss'],
})
export class PermitStatusChangeDialogComponent {
    form: FormGroup;
    isConditional: boolean;
    isTerminal: boolean;

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<PermitStatusChangeDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PermitStatusChangeDialogData,
        private translate: TranslateService,
    ) {
        this.isConditional = data.targetStatus.type === PermitStatusType.CONDITIONALLY_APPROVED;
        this.isTerminal = data.targetStatus.terminal === true;
        this.form = this.fb.group({
            reason: ['', Validators.required],
            conditions: ['', this.isConditional ? Validators.required : []],
        });
    }

    statusName(name: I18nString | string): string {
        if (!name) return '';
        if (typeof name === 'string') return name;
        const lang = this.translate.currentLang;
        return name[lang] || name.fr || name.en || '';
    }

    getChipBackground(color: string): string {
        if (!color) return 'rgba(0,0,0,0.05)';
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r},${g},${b},0.15)`;
    }

    confirm(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const { value } = this.form;
        this.dialogRef.close({
            reason: value.reason,
            conditions: this.isConditional ? value.conditions : undefined,
        });
    }

    cancel(): void {
        this.dialogRef.close(undefined);
    }
}
